import { useEffect, useState, useRef } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";

/**
 * Always-on connection-status pill (bottom-right).
 *
 * - "Connecting…" while the very first /api/auth/health call is in flight.
 * - "Live"  (green dot) when the backend is reachable.
 * - "Deploying new version" (amber, expanded) after 2 consecutive failures.
 * - "Back online" (green, expanded, 2.5s) on recovery, then collapses to "Live".
 *
 * The pill is dismissible (X button) so power users who don't want the chrome
 * can hide it for the session — but it auto-reappears the next time the
 * backend goes down so the deploy-window confusion can't recur.
 */
const STATUS = {
  CONNECTING: "connecting",
  LIVE: "live",
  DOWN: "down",
  RECOVERING: "recovering",
};

export default function DeployBanner() {
  const [status, setStatus] = useState(STATUS.CONNECTING);
  const [hidden, setHidden] = useState(false);
  const failCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        await api.get("/auth/health", { timeout: 6000 });
        if (cancelled) return;
        failCountRef.current = 0;
        setStatus((prev) => {
          if (prev === STATUS.DOWN) {
            // Show "Back online" then collapse to "Live"
            setTimeout(() => {
              if (!cancelled) setStatus(STATUS.LIVE);
            }, 2500);
            return STATUS.RECOVERING;
          }
          return STATUS.LIVE;
        });
      } catch (e) {
        if (cancelled) return;
        // 4xx = backend is up, just rejecting — don't trip the banner.
        const isUp = e?.response && e.response.status < 500;
        if (isUp) {
          failCountRef.current = 0;
          setStatus(STATUS.LIVE);
          return;
        }
        failCountRef.current += 1;
        if (failCountRef.current >= 2) {
          setStatus(STATUS.DOWN);
          // Force-reveal the pill if user had hidden it during a healthy state
          setHidden(false);
        }
      }
    };

    poll();
    const id = setInterval(poll, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (hidden) return null;

  // Compact "Live" / "Connecting" pill
  if (status === STATUS.LIVE || status === STATUS.CONNECTING) {
    const isLive = status === STATUS.LIVE;
    return (
      <div
        className="fixed bottom-4 right-4 z-[9999] pointer-events-auto"
        data-testid="deploy-banner"
        data-status={status}
      >
        <div
          className="flex items-center gap-2 rounded-full px-3 py-1.5 bg-black/70 border border-white/10 backdrop-blur-md shadow-lg group hover:bg-black/85 transition-all"
          title={isLive ? "Backend connection is healthy" : "Checking backend…"}
        >
          <span className="relative flex h-2 w-2">
            {isLive && (
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
            )}
            <span
              className={`relative inline-flex rounded-full h-2 w-2 ${
                isLive ? "bg-emerald-400" : "bg-amber-300 animate-pulse"
              }`}
            />
          </span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono">
            {isLive ? "Live" : "Connecting"}
          </span>
          <button
            onClick={() => setHidden(true)}
            className="ml-0.5 -mr-1 p-0.5 text-zinc-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
            title="Hide for this session"
            data-testid="deploy-banner-hide"
          >
            <Icons.X size={11} />
          </button>
        </div>
      </div>
    );
  }

  // Expanded outage / recovery banner
  const recovering = status === STATUS.RECOVERING;
  return (
    <div
      className="fixed bottom-4 right-4 z-[9999] max-w-sm pointer-events-auto"
      data-testid="deploy-banner"
      data-status={status}
    >
      <div
        className={`flex items-start gap-3 rounded-xl px-4 py-3 shadow-2xl border backdrop-blur-md transition-all ${
          recovering
            ? "bg-emerald-500/15 border-emerald-400/40"
            : "bg-amber-500/15 border-amber-300/40"
        }`}
      >
        {recovering ? (
          <Icons.CheckCircle2 size={18} className="text-emerald-300 mt-0.5 shrink-0" />
        ) : (
          <Icons.Loader2 size={18} className="text-amber-300 animate-spin mt-0.5 shrink-0" />
        )}
        <div className="text-[13px] leading-snug flex-1">
          <div
            className={`font-medium mb-0.5 ${
              recovering ? "text-emerald-200" : "text-amber-200"
            }`}
          >
            {recovering ? "Back online" : "Deploying new version"}
          </div>
          <div className="text-[11px] text-zinc-300/90">
            {recovering
              ? "Backend is reachable again — try your last action."
              : "Saves may briefly fail. The app will auto-recover in ~60 seconds."}
          </div>
        </div>
      </div>
    </div>
  );
}
